/**
 * @file DomainConnectGuide.tsx
 * @description «Подключение <домен>» — три шага (phase19.28 этап А;
 * uiux-platform-shops §5; шаблон devops-domains-routing §3.1):
 * 1 DNS у регистратора → 2 хост в NPM (делает владелец платформы, после
 * DNS ✓) → 3 проверка. Данные шаблона — `buildDomainGuide`, здесь только
 * разметка. У поддомена платформы шаг DNS уже выполнен (wildcard). Шаг 2
 * не блокируется — только предупреждение.
 */

import type { ReactNode } from "react";

import type { ShopDomain } from "../../../lib/api-platform-shops";
import { usePlatformEdgeConfig } from "../../../hooks/platform/usePlatformEdgeConfig";
import { buildDomainGuide } from "../../../lib/platform/npm-guide";
import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { CopyButton } from "./CopyButton";
import { DomainCheckMarks } from "./DomainCheckMarks";

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span
        aria-hidden
        className="type-body flex size-7 shrink-0 items-center justify-center rounded-full bg-accent font-semibold text-accent-foreground"
      >
        {n}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <h3 className="type-card-title">{title}</h3>
        {children}
      </div>
    </li>
  );
}

interface Props {
  domain: ShopDomain;
  checking: boolean;
  onRecheck: () => void;
  /** Отказ последней проверки (429, сеть) — под кнопкой, с aria-live. */
  checkError: string | null;
}

export function DomainConnectGuide({ domain, checking, onRecheck, checkError }: Props) {
  const edge = usePlatformEdgeConfig();
  const guide = buildDomainGuide(domain.host, domain.kind, edge);
  const dns = domain.lastCheck?.dns;
  return (
    <ol className="flex flex-col gap-5" aria-label={`Подключение ${guide.host}`}>
      <Step n={1} title="DNS у регистратора домена">
        {guide.isSubdomain ? (
          <p className="type-body">✓ Готово: wildcard {guide.wildcard} уже указывает на сервер платформы.</p>
        ) : (
          <>
            {/* Записи DNS — моноширинным блоком в две строки: «A имя» и значение; длинное имя переносится (QA D-N3). */}
            <ul className="flex flex-col divide-y divide-[var(--border)] rounded-lg bg-surface-low">
              {guide.dns.map((record) => (
                <li key={`${record.name}-${record.value}`} className="flex items-center gap-2 pl-3">
                  <code className="flex min-w-0 flex-1 flex-col py-2 font-mono text-sm">
                    <span className="break-all text-on-surface-variant">
                      {record.type} {record.name}
                    </span>
                    <span className="break-all font-medium">{record.value}</span>
                  </code>
                  <CopyButton value={record.value} label={`IP для ${record.name}`} />
                </li>
              ))}
            </ul>
            <p className="type-caption text-on-surface-variant">Для www — {guide.cnameHint}.</p>
            {dns && !dns.ok ? (
              <p className="type-body text-[var(--destructive-text)]">
                Сейчас: ✗ {guide.host} → {dns.ips?.length ? dns.ips.join(", ") : "записей нет"}
              </p>
            ) : null}
          </>
        )}
      </Step>
      <Step n={2} title="Хост в NPM — делает владелец платформы">
        {!guide.isSubdomain ? (
          <NoticeStrip tone="warning">Сначала дождитесь DNS ✓: иначе Let's Encrypt откажет (5 попыток в час).</NoticeStrip>
        ) : null}
        <ul className="flex flex-col gap-2">
          {guide.npm.map((line) => (
            <li key={line.key} className="type-body flex flex-col gap-1">
              <span>
                <span className="font-medium">{line.title}:</span> <span className="break-words">{line.text}</span>
              </span>
              {line.copy ? (
                <span className="flex flex-wrap items-center gap-1">
                  {line.copy.map((value) => (
                    <span key={value} className="type-caption inline-flex items-center gap-1 rounded-md bg-surface-low pl-2">
                      <span className="break-all">{value}</span>
                      <CopyButton value={value} label={value} />
                    </span>
                  ))}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center gap-2">
          <CopyButton value={guide.plain} label="Скопировать всю памятку" withText />
          {guide.docUrl ? (
            <a href={guide.docUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center font-medium text-primary hover:underline">
              Памятка целиком ↗
            </a>
          ) : null}
        </div>
      </Step>
      <Step n={3} title="Проверка">
        <DomainCheckMarks check={domain.lastCheck} expectedIps={edge.publicIps} />
        <Button variant="soft" className="self-start" onClick={onRecheck} loading={checking}>
          Проверить сейчас
        </Button>
        <p className="type-caption text-[var(--destructive-text)]" aria-live="polite">
          {checkError ?? ""}
        </p>
      </Step>
    </ol>
  );
}
